import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Header } from '@/components/layout/Header';
import { ChannelBadge } from '@/components/common/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useApi } from '@/hooks/useApi';
import { toast } from 'sonner';
import {
  Sparkles,
  Calendar,
  Building2,
  User,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Loader2
} from 'lucide-react';

export const EligibleBookingsPage = () => {
  const api = useApi();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const redirectTimer = useRef(null);

  const fetchBookings = useCallback(async () => {
    try {
      const response = await api.get('/bookings/eligible-for-review?since=2026-02-28');
      setBookings(response.data);
      setSelected([]);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api]);

  useEffect(() => {
    fetchBookings();
    return () => { if (redirectTimer.current) clearTimeout(redirectTimer.current); };
  }, [fetchBookings]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchBookings();
  };

  const toggleSelect = (id) => {
    setSelected(prev => 
      prev.includes(id) 
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selected.length === bookings.length) {
      setSelected([]);
    } else {
      setSelected(bookings.map(b => b.id));
    }
  };

  const handleCreateJobs = async () => {
    if (selected.length === 0) {
      toast.error('Sélectionnez au moins une réservation');
      return;
    }

    setCreating(true);
    try {
      const response = await api.post('/review-jobs/create-batch', selected);
      toast.success(
        <div>
          <p className="font-medium">{response.data.created} job(s) créé(s) !</p>
          <p className="text-sm mt-1">L'IA génère les brouillons en arrière-plan...</p>
        </div>,
        { duration: 5000 }
      );
      
      // Redirect to review jobs list after a moment
      redirectTimer.current = setTimeout(() => {
        navigate('/guest-reviews');
      }, 2000);
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  return (
    <MainLayout>
      <Header
        title="Réservations à traiter"
        subtitle="Réservations Airbnb avec checkout depuis le 28 février - sans avis rédigé"
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />

      <div className="p-6 space-y-6 animate-fadeIn" data-testid="eligible-bookings-page">
        {/* Info banner */}
        <Card className="bg-gradient-to-r from-[#FFF0F1] to-[#FFE8EA] border-[#FF5A6E]/20">
          <CardContent className="p-4 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#FF5A6E]/10 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-5 w-5 text-[#FF5A6E]" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Génération d'avis voyageurs - Airbnb uniquement</h3>
              <p className="text-sm text-slate-600 mt-1">
                Seules les réservations Airbnb sont affichées. Sur Booking.com, il n'est pas possible 
                de laisser un avis sur le voyageur. Pour répondre aux avis des voyageurs (Airbnb et Booking), 
                utilisez l'extension Chrome directement depuis la page d'avis.
              </p>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-12 text-slate-400">Chargement...</div>
        ) : bookings.length === 0 ? (
          <Card className="bg-white border-slate-100">
            <CardContent className="py-12 text-center">
              <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
              <p className="text-slate-600 font-medium">Tout est à jour !</p>
              <p className="text-sm text-slate-400 mt-1">
                Aucune réservation en attente d'avis
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Selection header */}
            <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Checkbox 
                      checked={selected.length === bookings.length}
                      onCheckedChange={selectAll}
                      data-testid="select-all-checkbox"
                    />
                    <span className="text-sm text-slate-600">
                      {selected.length > 0 
                        ? `${selected.length} sélectionné(s)`
                        : `${bookings.length} réservation(s) en attente`
                      }
                    </span>
                  </div>
                  <Button
                    onClick={handleCreateJobs}
                    disabled={selected.length === 0 || creating}
                    className="bg-[#FF5A6E] hover:bg-[#FF3B55] text-white"
                    data-testid="create-jobs-btn"
                  >
                    {creating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Création...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-4 w-4" />
                        Générer {selected.length > 0 ? `${selected.length} avis` : 'les avis'}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Bookings list */}
            <div className="space-y-3">
              {bookings.map((booking) => (
                <Card 
                  key={booking.id}
                  className={`bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] transition-all cursor-pointer ${
                    selected.includes(booking.id) ? 'ring-2 ring-[#FF5A6E] border-[#FF5A6E]' : ''
                  } ${booking.has_issues ? 'border-l-4 border-l-red-400' : ''}`}
                  onClick={() => toggleSelect(booking.id)}
                  data-testid={`booking-${booking.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <Checkbox 
                        checked={selected.includes(booking.id)}
                        onCheckedChange={() => toggleSelect(booking.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900">{booking.guest_name}</h3>
                          <ChannelBadge channel={booking.channel} />
                          {booking.has_issues && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700" data-testid={`issue-badge-${booking.id}`}>
                              <AlertTriangle size={10} />
                              Sur mesure
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Building2 size={14} />
                            {booking.property_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            Checkout: {booking.check_out}
                          </span>
                        </div>
                        {booking.has_issues && booking.issue_hint && (
                          <p className="text-xs text-red-500 mt-1.5 line-clamp-1">{booking.issue_hint}</p>
                        )}
                      </div>

                      <div className="text-sm text-slate-400">
                        {booking.check_in} → {booking.check_out}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default EligibleBookingsPage;
